import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, Chip, Radio } from '@mui/material';
import { useMemo, useEffect, useState, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import AppButton from '../../../../../../../components/common/AppButton';
import AppDialog from '../../../../../../../components/common/AppDialog';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import MUIIconCheckboxSelect, {
  IconCheckboxOption,
} from '../../../../../../../components/common/MUIIconCheckboxSelect';
import AppSwitch from '../../../../../../../components/common/AppSwitch';
import BranchTreeAutocomplete from '../../../../../../../components/common/BranchTreeAutocomplete';
import UserPermissionList from '../../../../../../users/components/UserScreens/components/UserPermissionList';
import { SVG } from '../../../../../../../constants/assetLists';
import { COLORS, ICONS } from '../../../../../../users/data';
import { useOnBoardingContext } from '../../../../../../../context/onBoarding/OnBoardingContextProvider';
import { handleGetPermissionsTreeService } from '../../../../../../../services/permissions';
import { handleCheckEmailsExistService } from '../../../../../../../services/on-boarding';
import QUERY_KEYS from '../../../../../../../constants/queryKeys';
import { ANNOUNCEMENT_TYPES } from '../../../../../../operations/components/Modals/AddAnnouncement/data';
import {
  DepartmentFormValues,
  NotifyPlaceSelection,
  OnboardingUserFormValues,
  RoleFormValues,
} from '../../../types';
import NotifyDeptTreeSelect, {
  buildNotifyPlaceSelections,
  ClientOption,
} from './components/NotifyDeptTreeSelect';
import PersonalNotifPanel from './components/PersonalNotifPanel';

const COUNTRY_CODES = [
  { label: '972', value: '972', icon: SVG.israelFlagIcon },
];

const phoneSchema = z
  .string({ error: 'שדה חובה!' })
  .min(1, 'שדה חובה!')
  .transform((s) => s.replace(/\D/g, ''))
  .refine((val) => val.length === 9 || val.length === 10, {
    message: 'מספר טלפון חייב להיות באורך 9 או 10 ספרות',
  });

const dialogSchema = z
  .object({
    status: z.enum(['active', 'inactive']),
    firstName: z.string().min(1, 'שדה חובה!'),
    lastName: z.string().min(1, 'שדה חובה!'),
    clientIds: z.array(z.string()).min(1, 'נדרש ארגון אחד לפחות!'),
    mainBranchId: z.string().optional(),
    otherBranchIds: z.array(z.string()),
    subBranchIds: z.array(z.string()),
    departmentId: z.union([
      z.literal('all'),
      z.array(z.string()).min(1, 'שדה חובה!'),
    ]),
    roleId: z.string().min(1, 'שדה חובה!'),
    countryCode: z.string().min(1, 'שדה חובה!'),
    phone: phoneSchema,
    email: z.string().email('אימייל לא תקין').min(1, 'שדה חובה!'),
    sendPassword: z.boolean(),
    sendPasswordVia: z.string().optional(),
    birthday: z.string().optional(),
    userPermissions: z.array(z.string()),
    associatedGroups: z.array(z.string()).optional(),
    notifyFromDepts: z
      .array(
        z.object({
          clientId: z.string(),
          branchId: z.string().optional(),
          subBranchId: z.string().optional(),
          departmentIds: z.union([z.literal('all'), z.array(z.string())]),
        }),
      )
      .min(1, 'שדה חובה!'),
    notifyQuickDepts: z
      .array(
        z.object({
          clientId: z.string(),
          branchId: z.string().optional(),
          subBranchId: z.string().optional(),
          departmentIds: z.union([z.literal('all'), z.array(z.string())]),
        }),
      )
      .min(1, 'שדה חובה!'),
    notifyTypes: z.array(z.string()).min(1, 'שדה חובה!'),
    enablePersonalNotifications: z.boolean(),
  })
  .refine((data) => !data.sendPassword || !!data.sendPasswordVia, {
    message: 'שדה חובה!',
    path: ['sendPasswordVia'],
  });

type DialogFormValues = z.infer<typeof dialogSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (data: Omit<OnboardingUserFormValues, 'id'>) => void;
  selectedUser?: OnboardingUserFormValues | null;
  departments: DepartmentFormValues[];
  roles: RoleFormValues[];
  clients: ClientOption[];
  existingUsers: OnboardingUserFormValues[];
  initialLocation: {
    clientId: string;
    branchId?: string;
    subBranchId?: string;
  };
};

const DEFAULT_VALUES: DialogFormValues = {
  status: 'inactive',
  firstName: '',
  lastName: '',
  clientIds: [],
  mainBranchId: '',
  otherBranchIds: [],
  subBranchIds: [],
  departmentId: [],
  roleId: '',
  countryCode: '972',
  phone: '',
  email: '',
  sendPassword: false,
  sendPasswordVia: '',
  birthday: '',
  userPermissions: [],
  associatedGroups: [],
  notifyFromDepts: [],
  notifyQuickDepts: [],
  notifyTypes: [],
  enablePersonalNotifications: false,
};

function AddEditUserDialog({
  open,
  onClose,
  onSave,
  selectedUser,
  departments,
  roles,
  clients,
  existingUsers,
  initialLocation,
}: Props) {
  const { allRoleTypes } = useOnBoardingContext();
  const [showOtherBranchesField, setShowOtherBranchesField] = useState(false);
  const [showPermissionList, setShowPermissionList] = useState(false);
  const [permissionsSnapshot, setPermissionsSnapshot] = useState<string[]>([]);
  const [showPersonalNotif, setShowPersonalNotif] = useState(false);
  const [personalNotifSnapshot, setPersonalNotifSnapshot] = useState<{
    fromDepts: NotifyPlaceSelection[];
    quickDepts: NotifyPlaceSelection[];
    types: string[];
  }>({ fromDepts: [], quickDepts: [], types: [] });
  const roleChangedByUser = useRef(false);
  const deptChangedByUser = useRef(false);

  const { data: allGroupedPermissions } = useQuery({
    queryKey: [QUERY_KEYS.GET_PERMISSION_TREE],
    queryFn: handleGetPermissionsTreeService,
    enabled: !!open,
  });

  const checkEmailsExistMutation = useMutation({
    mutationKey: [QUERY_KEYS.CHECK_EMAILS_EXIST],
    mutationFn: handleCheckEmailsExistService,
  });

  const {
    control,
    watch,
    setValue,
    setError,
    getValues,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DialogFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(dialogSchema),
  });
  useEffect(() => {
    if (!open) return;
    if (selectedUser) {
      reset({
        status: selectedUser.status,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        clientIds: selectedUser.clientIds,
        mainBranchId: selectedUser.branchIds[0] || '',
        otherBranchIds: selectedUser.branchIds,
        subBranchIds: selectedUser.subBranchIds ?? [],
        departmentId: selectedUser.departmentId,
        roleId: selectedUser.roleId,
        countryCode: selectedUser.countryCode || '972',
        phone: selectedUser.phone,
        email: selectedUser.email,
        sendPassword: selectedUser.sendPassword,
        sendPasswordVia: selectedUser.sendPasswordVia,
        birthday: selectedUser.birthday,
        userPermissions: selectedUser.userPermissions,
        associatedGroups: selectedUser.associatedGroups ?? [],
        notifyFromDepts: selectedUser.notifyFromDepts ?? [],
        notifyQuickDepts: selectedUser.notifyQuickDepts ?? [],
        notifyTypes: selectedUser.notifyTypes ?? [],
        enablePersonalNotifications:
          selectedUser.enablePersonalNotifications ?? false,
      });
      setShowOtherBranchesField(
        (selectedUser.branchIds?.length ?? 0) > 1 ||
          (selectedUser.subBranchIds?.length ?? 0) > 0,
      );
    } else {
      reset({
        ...DEFAULT_VALUES,
        clientIds: [initialLocation.clientId],
        mainBranchId: initialLocation.branchId ?? '',
        otherBranchIds: initialLocation.branchId
          ? [initialLocation.branchId]
          : [],
        subBranchIds: initialLocation.subBranchId
          ? [initialLocation.subBranchId]
          : [],
      });
      setShowOtherBranchesField(Boolean(initialLocation.subBranchId));
    }
    setShowPermissionList(false);
    setShowPersonalNotif(false);
    setPermissionsSnapshot([]);
  }, [initialLocation, open, selectedUser, reset]);

  const clientIds = watch('clientIds');
  const otherBranchIds = watch('otherBranchIds');
  const subBranchIds = watch('subBranchIds');
  const departmentId = watch('departmentId');
  const roleId = watch('roleId');
  const userPermissions = watch('userPermissions');
  const mainBranchId = watch('mainBranchId');
  const notifyFromDepts = watch('notifyFromDepts');
  const notifyQuickDepts = watch('notifyQuickDepts');
  const notifyTypes = watch('notifyTypes');

  useEffect(() => {
    if (!roleChangedByUser.current) return;
    roleChangedByUser.current = false;
    const role = roles.find((r) => r.id === roleId);
    if (
      role?.permissions?.length &&
      role?.permissions?.includes('customize_notifications')
    ) {
      setValue('enablePersonalNotifications', true, { shouldDirty: true });
    } else {
      setValue('enablePersonalNotifications', false, { shouldDirty: true });
    }
    if (role?.permissions?.length) {
      setValue('userPermissions', role.permissions);
    }
  }, [roleId, roles, setValue]);

  // Reset role and permissions when the user actively changes department
  useEffect(() => {
    if (!deptChangedByUser.current) return;
    deptChangedByUser.current = false;
    setValue('roleId', '', { shouldDirty: true, shouldValidate: true });
    setValue('userPermissions', [], { shouldDirty: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId]);

  // Strip notify selections that reference clients/branches no longer selected
  useEffect(() => {
    const clientSet = new Set(clientIds ?? []);
    const branchSet = new Set(otherBranchIds ?? []);
    const subBranchSet = new Set(subBranchIds ?? []);

    const clean = (sel: NotifyPlaceSelection[]) =>
      sel.filter((s) => {
        if (!clientSet.has(s.clientId)) return false;
        if (s.branchId && !branchSet.has(s.branchId)) return false;
        if (s.subBranchId && !subBranchSet.has(s.subBranchId)) return false;
        return true;
      });

    const cleanedFrom = clean(notifyFromDepts ?? []);
    const cleanedQuick = clean(notifyQuickDepts ?? []);

    if (cleanedFrom.length !== (notifyFromDepts ?? []).length)
      setValue('notifyFromDepts', cleanedFrom, {
        shouldDirty: true,
        shouldValidate: true,
      });
    if (cleanedQuick.length !== (notifyQuickDepts ?? []).length)
      setValue('notifyQuickDepts', cleanedQuick, {
        shouldDirty: true,
        shouldValidate: true,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds, otherBranchIds, subBranchIds]);

  const availableBranches = useMemo(
    () =>
      clients
        .filter((c) => clientIds.includes(c.id))
        .flatMap((c) => c.branches),
    [clients, clientIds],
  );

  const combinedDepartments = useMemo(() => {
    const branchSet = new Set(otherBranchIds ?? []);
    const subBranchSet = new Set(subBranchIds ?? []);
    const map = new Map<
      string,
      DepartmentFormValues & { groupLabel: string }
    >();
    const add = (list: DepartmentFormValues[], groupLabel: string) =>
      list.forEach((d) => map.set(d.id, { ...d, groupLabel }));

    clients
      .filter((c) => clientIds.includes(c.id))
      .forEach((c) => {
        if (branchSet.size === 0) {
          // No branch selected → all departments in the client tree
          add(c.departments, c.name);
          c.branches.forEach((b) => {
            add(b.departments, b.name);
            b.subBranches.forEach((sb) => add(sb.departments, sb.name));
          });
        } else {
          // Branch(es) selected → no client-level departments.
          // If a selected branch has selected subBranches, show only those subBranches' departments.
          // Otherwise show that branch's departments + all its subBranches' departments.
          c.branches.forEach((b) => {
            if (!branchSet.has(b.id)) return;
            const selectedSubs = b.subBranches.filter((sb) =>
              subBranchSet.has(sb.id),
            );
            if (selectedSubs.length > 0) {
              selectedSubs.forEach((sb) => add(sb.departments, sb.name));
            } else {
              add(b.departments, b.name);
              b.subBranches.forEach((sb) => add(sb.departments, sb.name));
            }
          });
        }
      });
    return Array.from(map.values()).sort((a, b) =>
      a.groupLabel.localeCompare(b.groupLabel),
    );
  }, [clients, clientIds, otherBranchIds, subBranchIds]);

  const combinedRoles = useMemo(() => {
    const branchSet = new Set(otherBranchIds ?? []);
    const subBranchSet = new Set(subBranchIds ?? []);
    const map = new Map<string, RoleFormValues & { groupLabel: string }>();
    const add = (list: RoleFormValues[] | undefined, groupLabel: string) =>
      (list ?? []).forEach((r) => map.set(r.id, { ...r, groupLabel }));

    clients
      .filter((c) => clientIds.includes(c.id))
      .forEach((c) => {
        if (branchSet.size === 0) {
          add(c.roles, c.name);
          c.branches.forEach((b) => {
            add(b.roles, b.name);
            b.subBranches.forEach((sb) => add(sb.roles, sb.name));
          });
        } else {
          c.branches.forEach((b) => {
            if (!branchSet.has(b.id)) return;
            const selectedSubs = b.subBranches.filter((sb) =>
              subBranchSet.has(sb.id),
            );
            if (selectedSubs.length > 0) {
              selectedSubs.forEach((sb) => add(sb.roles, sb.name));
            } else {
              add(b.roles, b.name);
              b.subBranches.forEach((sb) => add(sb.roles, sb.name));
            }
          });
        }
      });
    return Array.from(map.values()).sort((a, b) =>
      a.groupLabel.localeCompare(b.groupLabel),
    );
  }, [clients, clientIds, otherBranchIds, subBranchIds]);

  const filteredClientsForNotify = useMemo(() => {
    if (!clientIds.length) return [];
    const branchSet = new Set(otherBranchIds ?? []);
    const subBranchSet = new Set(subBranchIds ?? []);
    return clients
      .filter((c) => clientIds.includes(c.id))
      .map((c) => ({
        ...c,
        branches: c.branches
          .filter((b) => branchSet.has(b.id))
          .map((b) => ({
            ...b,
            subBranches: b.subBranches.filter((sb) => subBranchSet.has(sb.id)),
          })),
      }));
  }, [clients, clientIds, otherBranchIds, subBranchIds]);

  const adaptedClientsForTree = useMemo(
    () =>
      clients
        .filter((c) => clientIds.includes(c.id))
        .map((c) => ({
          id: c.id,
          name: c.name,
          icon: SVG.clientIcon,
          isClient: true as const,
          branches: c.branches.map((b) => ({
            id: b.id,
            name: b.name,
            icon: SVG.clientIcon,
            isClient: false as const,
            isBranch: true as const,
            isSubBranch: false as const,
            clientId: c.id,
            subBranches: b.subBranches.map((sb) => ({
              id: sb.id,
              name: sb.name,
              icon: SVG.clientIcon,
              isClient: false as const,
              isBranch: false as const,
              isSubBranch: true as const,
              clientId: c.id,
              branchId: b.id,
            })),
          })),
          isBranch: false as const,
          isSubBranch: false as const,
        })),
    [clients, clientIds],
  );

  const deptOptions = useMemo(
    () =>
      combinedDepartments?.length > 0
        ? [
            {
              label: 'כל המחלקות',
              value: 'all',
              icon: SVG.saveAllIcon,
              groupLabel: '',
            },
            ...combinedDepartments.map((d) => ({
              label: d.name || 'מחלקה ללא שם',
              value: d.id,
              icon: ICONS.find((i) => i.id === d.icon)?.icon || '',
              groupLabel: d.groupLabel,
            })),
          ]
        : [],
    [combinedDepartments],
  );

  const roleOptions = useMemo(() => {
    if (
      !departmentId ||
      (Array.isArray(departmentId) && departmentId.length === 0)
    )
      return [];
    if (
      departmentId === 'all' ||
      (Array.isArray(departmentId) && departmentId.includes('all'))
    )
      return combinedRoles;
    const ids = Array.isArray(departmentId) ? departmentId : [];
    return combinedRoles.filter((r) => ids.includes(r.departmentId));
  }, [combinedRoles, departmentId]);

  const deptValue = useMemo(() => {
    if (
      !departmentId ||
      (Array.isArray(departmentId) && departmentId.length === 0)
    )
      return [];
    if (
      departmentId === 'all' ||
      (Array.isArray(departmentId) && departmentId[0] === 'all')
    )
      return deptOptions;
    const ids = Array.isArray(departmentId) ? departmentId : [];
    return deptOptions.filter((o) => ids.includes(o.value));
  }, [departmentId, deptOptions]);

  const getRoleDisplayName = (roleType: string) =>
    allRoleTypes?.data?.find((rt) => rt._id === roleType)?.name?.he || roleType;

  const notifTypeOptions = useMemo(
    () => [
      { label: 'כל סוגי ההודעות', value: 'all', icon: SVG.saveAllIcon },
      ...ANNOUNCEMENT_TYPES.map((t) => ({
        label: t.title,
        value: t.value,
        icon: t.icon,
      })),
    ],
    [],
  );

  const realNotifTypeOptions = useMemo(
    () => notifTypeOptions.filter((o) => o.value !== 'all'),
    [notifTypeOptions],
  );

  const notifyTypeValue = useMemo(() => {
    if (!notifyTypes || notifyTypes.length === 0) return [];
    const allSelected = realNotifTypeOptions.every((o) =>
      notifyTypes.includes(o.value),
    );
    if (allSelected) return notifTypeOptions;
    return notifTypeOptions.filter((o) => notifyTypes.includes(o.value));
  }, [notifyTypes, notifTypeOptions, realNotifTypeOptions]);

  const handleModalClose = () => {
    reset(DEFAULT_VALUES);
    setShowOtherBranchesField(false);
    setShowPermissionList(false);
    setShowPersonalNotif(false);
    setPermissionsSnapshot([]);
    onClose();
  };

  const onSubmit = async (data: DialogFormValues) => {
    const emailLower = data.email.trim().toLowerCase();
    const duplicate = existingUsers.find(
      (u) =>
        u.email.trim().toLowerCase() === emailLower &&
        u.id !== selectedUser?.id,
    );
    if (duplicate) {
      setError('email', { message: 'אימייל זה כבר קיים במערכת' });
      return;
    }

    const emailChanged =
      !selectedUser || selectedUser.email.trim().toLowerCase() !== emailLower;
    if (emailChanged) {
      try {
        const { existingEmails } = await checkEmailsExistMutation.mutateAsync([
          emailLower,
        ]);
        if (existingEmails.length > 0) {
          setError('email', { message: 'אימייל זה כבר קיים במערכת' });
          return;
        }
      } catch {
        return;
      }
    }

    const { mainBranchId: mb, otherBranchIds, ...rest } = data;
    const branchIds = [...new Set([...(mb ? [mb] : []), ...otherBranchIds])];
    onSave({
      ...rest,
      email: emailLower,
      branchIds,
      sendPasswordVia: rest.sendPasswordVia ?? '',
      birthday: rest.birthday ?? '',
    });
    reset(DEFAULT_VALUES);
    setShowOtherBranchesField(false);
  };

  const handleDepartmentsChange = (
    e: React.SyntheticEvent,
    value: { value: string }[] | null,
  ) => {
    const text = e.currentTarget.textContent;
    const isAllSelectedByText = text === 'כל המחלקות';
    const currentDepartments = (
      Array.isArray(departmentId) ? departmentId : []
    ) as string[];

    const selectedValues = value?.map((item) => item.value) || [];
    const realDeptOptions =
      deptOptions?.filter((d: any) => d.value !== 'all') || [];
    const allDeptSelected =
      realDeptOptions.length > 0 &&
      realDeptOptions.every((dept: any) => selectedValues.includes(dept.value));
    const hasAllInSelection = selectedValues.includes('all');
    const hadAllPreviously = currentDepartments.includes('all');

    let newValue: string[] = [];
    if (hadAllPreviously && !hasAllInSelection) {
      newValue = []; // reset
    } else if (hadAllPreviously) {
      newValue = selectedValues.filter((v) => v !== 'all');
    } else if (isAllSelectedByText || hasAllInSelection || allDeptSelected) {
      newValue = ['all'];
    } else {
      newValue = selectedValues;
    }

    deptChangedByUser.current = true;
    setValue('departmentId', newValue, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const notifySelections = buildNotifyPlaceSelections(
      newValue,
      filteredClientsForNotify,
    );
    setValue('notifyFromDepts', notifySelections, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('notifyQuickDepts', notifySelections, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleNotifyTypesChange = (
    _: React.SyntheticEvent,
    value: { value: string }[] | null,
  ) => {
    const isAllClickedByText = value?.find((v) => v.value === 'all')
      ? true
      : false;
    const currentValues = notifyTypes || [];

    const selectedValues = value?.map((item) => item.value) || [];
    const allWasSelected =
      realNotifTypeOptions.length > 0 &&
      realNotifTypeOptions.every((o) => currentValues.includes(o.value));
    const hasAllInSelection = selectedValues.includes('all');
    const allNowSelected =
      realNotifTypeOptions.length > 0 &&
      realNotifTypeOptions.every((o) => selectedValues.includes(o.value));

    let newValue: string[];
    if (allWasSelected && !hasAllInSelection) {
      newValue = [];
    } else if (allWasSelected) {
      newValue = selectedValues.filter((v) => v !== 'all');
    } else if (isAllClickedByText || hasAllInSelection || allNowSelected) {
      newValue = realNotifTypeOptions.map((o) => o.value);
    } else {
      newValue = selectedValues.filter((v) => v !== 'all');
    }

    setValue('notifyTypes', newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onCancelPersonalNotifChanges = () => {
    setValue('notifyFromDepts', personalNotifSnapshot.fromDepts);
    setValue('notifyQuickDepts', personalNotifSnapshot.quickDepts);
    setValue('notifyTypes', personalNotifSnapshot.types);
    setShowPersonalNotif(false);
  };

  return (
    <AppDialog
      showModel={open}
      title={selectedUser ? 'עריכת משתמש' : 'הוספת משתמש'}
      titleIcon={
        <img
          src={selectedUser ? SVG.outlinePencilDarkIcon : SVG.darkStarIcon}
          alt="icon"
          className="w-[14px] h-[14px] object-contain"
        />
      }
      onClose={handleModalClose}
      showHeaderLine={false}
      width={717}
    >
      <div className="flex flex-col gap-4 pt-4">
        {showPermissionList ? (
          <div className="flex-1 flex flex-col gap-4 extraSmall:min-h-[500px] lg:min-h-[530px] 2xl:min-h-[600px] extraSmall:max-h-[500px] lg:max-h-[530px] 2xl:max-h-[600px] overflow-y-auto px-[40px]">
            <UserPermissionList
              allGroupedPermissions={allGroupedPermissions}
              selectedPermissions={userPermissions || []}
              onPermissionsChange={(perms) =>
                setValue('userPermissions', perms, { shouldDirty: true })
              }
              byPermName={true}
            />
          </div>
        ) : showPersonalNotif ? (
          <PersonalNotifPanel
            clients={filteredClientsForNotify}
            notifyFromDepts={notifyFromDepts}
            onFromDeptsChange={(val) =>
              setValue('notifyFromDepts', val, { shouldDirty: true })
            }
            notifyQuickDepts={notifyQuickDepts}
            onQuickDeptsChange={(val) =>
              setValue('notifyQuickDepts', val, { shouldDirty: true })
            }
            notifyTypes={notifyTypes}
            onTypesChange={(val) =>
              setValue('notifyTypes', val, { shouldDirty: true })
            }
            onBackPress={onCancelPersonalNotifChanges}
          />
        ) : (
          <div className="flex-1 flex flex-col gap-4 extraSmall:max-h-[500px] lg:max-h-[530px] 2xl:max-h-[600px] overflow-y-auto px-[40px]">
            {/* {selectedUser && (
              <div className="flex justify-end">
                <div className="flex items-center gap-2">
                  <Controller
                    name="status"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <AppSwitch
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(e.target.checked ? "active" : "inactive")
                        }
                        checked={value === "active"}
                      />
                    )}
                  />
                  <p
                    className={`m-0 ${watch("status") === "active" ? "font-medium" : ""}`}
                  >
                    משתמש פעיל
                  </p>
                </div>
              </div>
            )} */}

            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <div className="flex items-center">
                  <div className="w-10">
                    <img
                      src={SVG.userDarkIcon}
                      className="w-[16px] h-[16px] object-contain"
                    />
                  </div>
                  <AppTextInput
                    type="text"
                    label="שם פרטי"
                    placeholder="שם פרטי"
                    className="border-0"
                    required
                    {...field}
                    error={errors.firstName?.message || ''}
                  />
                </div>
              )}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <div className="flex items-center">
                  <div className="w-10" />
                  <AppTextInput
                    type="text"
                    label="שם משפחה"
                    placeholder="שם משפחה"
                    className="border-0"
                    required
                    {...field}
                    error={errors.lastName?.message || ''}
                  />
                </div>
              )}
            />
            <p className="my-2 font-medium">מידע כללי</p>
            <Controller
              name="clientIds"
              control={control}
              render={({ field: { value } }) => (
                <div className="flex items-center">
                  <div className="w-10">
                    <img
                      src={SVG.clientIcon}
                      className="w-[24px] h-[24px] object-contain"
                    />
                  </div>
                  <AppMUIAutocomplete
                    label="ארגון"
                    size="small"
                    required
                    fullWidth
                    multiple
                    options={clients}
                    getOptionLabel={(o) => o.name || 'ארגון ללא שם'}
                    value={clients.filter((c) => (value || []).includes(c.id))}
                    onChange={(_e, vals) => {
                      setValue(
                        'clientIds',
                        vals.map((v) => v.id),
                        { shouldValidate: true, shouldDirty: true },
                      );
                      setValue('mainBranchId', '', { shouldDirty: true });
                      setValue('otherBranchIds', [], { shouldDirty: true });
                      setValue('subBranchIds', [], { shouldDirty: true });
                      setShowOtherBranchesField(false);
                      setValue('departmentId', [], { shouldDirty: true });
                      setValue('roleId', '', { shouldDirty: true });
                      setValue('notifyFromDepts', [], { shouldDirty: true });
                      setValue('notifyQuickDepts', [], { shouldDirty: true });
                    }}
                    error={!!errors.clientIds}
                    helperText={(errors.clientIds as any)?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="mainBranchId"
              control={control}
              render={({ field: { value } }) => (
                <div className="flex items-center">
                  <div className="w-10" />
                  <AppMUIAutocomplete
                    label="סניף ראשי"
                    size="small"
                    fullWidth
                    disabled={!clientIds?.length || !availableBranches.length}
                    options={availableBranches}
                    getOptionLabel={(o) => o.name || 'סניף ללא שם'}
                    value={
                      availableBranches.find((b) => b.id === value) || null
                    }
                    onChange={(_e, val) => {
                      if (val) {
                        setValue('mainBranchId', val.id, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('otherBranchIds', [val.id], {
                          shouldDirty: true,
                        });
                        setValue('subBranchIds', [], {
                          shouldDirty: true,
                        });
                      } else {
                        setValue('mainBranchId', '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                        setValue('otherBranchIds', [], { shouldDirty: true });
                        setValue('subBranchIds', [], { shouldDirty: true });
                      }
                      setValue('departmentId', [], { shouldDirty: true });
                      setValue('roleId', '', { shouldDirty: true });
                      setShowOtherBranchesField(false);
                    }}
                    error={!!errors.mainBranchId}
                    helperText={errors.mainBranchId?.message}
                  />
                </div>
              )}
            />

            {mainBranchId &&
              (availableBranches.length > 1 ||
                availableBranches.some(
                  (branch) => branch.subBranches.length,
                )) && (
                <div className="flex items-center">
                  <div className="w-10" />
                  <Controller
                    name="otherBranchIds"
                    control={control}
                    render={({ field: { value } }) =>
                      !showOtherBranchesField ? (
                        <AppButton
                          variant="secondary"
                          iconStart={
                            <div className="w-[12px] h-[12px]">
                              <img
                                src={SVG.addIcon}
                                className="w-full h-full object-contain"
                              />
                            </div>
                          }
                          onClick={() => setShowOtherBranchesField(true)}
                        >
                          הוספת סניף
                        </AppButton>
                      ) : (
                        <BranchTreeAutocomplete
                          allClients={adaptedClientsForTree as any}
                          value={[...(value || []), ...(subBranchIds || [])]}
                          onChange={(nodes) => {
                            const previousBranchIds =
                              getValues('otherBranchIds') || [];
                            const newBranchIds = (nodes as any[])
                              .filter((node) => node.isBranch)
                              .map((node) => node.id);
                            const newSubBranches = (nodes as any[]).filter(
                              (node) => node.isSubBranch,
                            );
                            const removedBranchIds = previousBranchIds.filter(
                              (id) => !newBranchIds.includes(id),
                            );
                            const filteredSubBranches = newSubBranches.filter(
                              (sub) => !removedBranchIds.includes(sub.branchId),
                            );
                            const parentBranchIds = filteredSubBranches.map(
                              (sub) => sub.branchId,
                            );

                            setValue(
                              'otherBranchIds',
                              Array.from(
                                new Set([...newBranchIds, ...parentBranchIds]),
                              ),
                              { shouldDirty: true, shouldValidate: true },
                            );
                            setValue(
                              'subBranchIds',
                              filteredSubBranches.map((sub) => sub.id),
                              { shouldDirty: true, shouldValidate: true },
                            );
                            setValue('departmentId', [], { shouldDirty: true });
                            setValue('roleId', '', { shouldDirty: true });
                          }}
                        />
                      )
                    }
                  />
                </div>
              )}

            <Controller
              name="departmentId"
              control={control}
              render={() => (
                <div className="flex items-center">
                  <div className="w-10" />
                  <MUIIconCheckboxSelect
                    label="מחלקה"
                    size="small"
                    fullWidth
                    required
                    disableCloseOnSelect
                    options={deptOptions}
                    groupBy={(option) => option.groupLabel || ''}
                    value={deptValue}
                    renderValue={(selectedOptions, getTagProps) => {
                      const allSelected =
                        (Array.isArray(departmentId) &&
                          departmentId?.[0] === 'all') ||
                        selectedOptions?.length === deptOptions.length;

                      const renderChip = (
                        option: IconCheckboxOption,
                        index = 0,
                      ) => (
                        <Chip
                          label={option.label}
                          icon={
                            <img
                              src={option.icon}
                              alt="icon"
                              className="w-[18px] h-[18px] object-contain"
                            />
                          }
                          {...getTagProps({ index })}
                        />
                      );

                      if (allSelected) {
                        const allOption = deptOptions.find(
                          (o) => o.value === 'all',
                        );
                        return allOption ? renderChip(allOption) : null;
                      }

                      return selectedOptions.map((option, index) =>
                        renderChip(option, index),
                      );
                    }}
                    onChange={handleDepartmentsChange}
                    error={!!errors.departmentId}
                    helperText={errors.departmentId?.message}
                  />
                </div>
              )}
            />

            <Controller
              name="roleId"
              control={control}
              render={({ field: { value } }) => (
                <div>
                  <div className="flex items-center">
                    <div className="w-10" />
                    <AppMUIAutocomplete
                      label="תפקיד"
                      size="small"
                      fullWidth
                      required
                      options={roleOptions}
                      groupBy={(option) => option.groupLabel}
                      getOptionLabel={(o) => getRoleDisplayName(o.roleType)}
                      renderValue={(val) =>
                        val && (
                          <div className="flex items-center gap-1">
                            {ICONS.find((i) => i.id === val.icon)?.icon && (
                              <img
                                src={ICONS.find((i) => i.id === val.icon)?.icon}
                                alt="icon"
                                className="w-[18px] h-[18px] object-contain me-2"
                              />
                            )}
                            <div
                              className="w-[24px] h-[24px] rounded-[3px] ml-2 border-1 border-[#707070]"
                              style={{
                                background:
                                  COLORS.find((c) => c.color === val.color)
                                    ?.color ?? '',
                              }}
                            />
                            {getRoleDisplayName(val.roleType)}
                          </div>
                        )
                      }
                      renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        return (
                          <li key={props.id} {...rest}>
                            {ICONS.find((i) => i.id === option.icon)?.icon && (
                              <img
                                src={
                                  ICONS.find((i) => i.id === option.icon)?.icon
                                }
                                alt="icon"
                                className="w-[18px] h-[18px] object-contain me-2"
                              />
                            )}
                            <div
                              className="w-[24px] h-[24px] rounded-[3px] ml-2 border-1 border-[#707070]"
                              style={{
                                background:
                                  COLORS.find((c) => c.color === option.color)
                                    ?.color ?? '',
                              }}
                            />
                            {getRoleDisplayName(option.roleType)}
                          </li>
                        );
                      }}
                      value={roleOptions.find((r) => r.id === value) || null}
                      onChange={(_e, val) => {
                        roleChangedByUser.current = true;
                        setValue('roleId', val?.id ?? '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      error={!!errors.roleId}
                      helperText={errors.roleId?.message}
                    />
                  </div>
                  {roleId && (
                    <div className="flex items-center mt-4">
                      <div className="w-10" />
                      <AppButton
                        variant="secondary"
                        iconStart={
                          <div className="w-[12px] h-[12px]">
                            <img
                              src={SVG.editGrayIcon}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        }
                        onClick={() => {
                          let perms = userPermissions?.length
                            ? userPermissions
                            : [];
                          if (!perms.length) {
                            const role = roles.find((r) => r.id === roleId);
                            perms = role?.permissions ?? [];
                            setValue('userPermissions', perms, {
                              shouldDirty: true,
                            });
                          }
                          setPermissionsSnapshot([...perms]);
                          setShowPermissionList(true);
                        }}
                      >
                        תפקיד - הגדרות אישיות{' '}
                      </AppButton>
                    </div>
                  )}
                </div>
              )}
            />

            <Controller
              name="associatedGroups"
              control={control}
              render={({ field: { value } }) => (
                <div className="flex items-center">
                  <div className="w-10" />
                  <AppMUIAutocomplete
                    label="שיוך לקבוצה"
                    size="small"
                    fullWidth
                    multiple
                    options={[]}
                    getOptionLabel={(o: string) => o}
                    value={value ?? []}
                    onChange={(_e, vals) =>
                      setValue('associatedGroups', vals as string[], {
                        shouldDirty: true,
                      })
                    }
                  />
                </div>
              )}
            />

            <p className="my-2 font-medium">הגדרת התראות</p>

            <div className="flex items-center">
              <div className="w-10" />
              <div className="flex-1 min-w-0">
                <NotifyDeptTreeSelect
                  label="מקבל התראות של מחלקות"
                  clients={filteredClientsForNotify}
                  value={notifyFromDepts}
                  onChange={(val) =>
                    setValue('notifyFromDepts', val, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  error={!!errors.notifyFromDepts}
                />
                {(errors.notifyFromDepts as any)?.message && (
                  <span className="text-raspberryRed text-[12px] mt-0.5 block">
                    {(errors.notifyFromDepts as any).message}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-10" />
              <div className="flex-1 min-w-0">
                <NotifyDeptTreeSelect
                  label="עד הודעות מהירות"
                  clients={filteredClientsForNotify}
                  value={notifyQuickDepts}
                  onChange={(val) =>
                    setValue('notifyQuickDepts', val, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  error={!!errors.notifyQuickDepts}
                />
                {(errors.notifyQuickDepts as any)?.message && (
                  <span className="text-raspberryRed text-[12px] mt-0.5 block">
                    {(errors.notifyQuickDepts as any).message}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-10" />
              <Controller
                name="notifyTypes"
                control={control}
                render={() => (
                  <MUIIconCheckboxSelect
                    required
                    label="מקבל התראות מסוג"
                    size="small"
                    fullWidth
                    disableCloseOnSelect
                    options={notifTypeOptions}
                    value={notifyTypeValue}
                    renderValue={(selectedOptions, getTagProps) => {
                      const allSelected =
                        realNotifTypeOptions.length > 0 &&
                        selectedOptions?.length === notifTypeOptions.length;

                      const renderChip = (
                        option: (typeof notifTypeOptions)[0],
                        index = 0,
                      ) => (
                        <Chip
                          label={option.label}
                          icon={
                            <img
                              src={option.icon}
                              alt="icon"
                              className="w-[18px] h-[18px] object-contain"
                            />
                          }
                          {...getTagProps({ index })}
                        />
                      );

                      if (allSelected) {
                        const allOption = notifTypeOptions.find(
                          (o) => o.value === 'all',
                        );
                        return allOption ? renderChip(allOption) : null;
                      }

                      return selectedOptions
                        .map((option, index) => ({ option, index }))
                        .filter(({ option }) => option.value !== 'all')
                        .map(({ option, index }) => renderChip(option, index));
                    }}
                    onChange={handleNotifyTypesChange}
                    error={!!errors.notifyTypes}
                    helperText={(errors.notifyTypes as any)?.message}
                  />
                )}
              />
            </div>

            <div className="flex items-center">
              <div className="w-10" />
              <div className="flex items-center justify-between gap-4 flex-1">
                <Controller
                  name="enablePersonalNotifications"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="flex items-center gap-2">
                      <AppSwitch
                        checked={value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(e.target.checked)
                        }
                      />
                      <span className="text-sm">
                        אפשר הגדרות אישיות לקבלת התראות
                      </span>
                    </div>
                  )}
                />
                <AppButton
                  variant="secondary"
                  py="12px"
                  iconStart={
                    <div className="w-[12px] h-[12px]">
                      <img
                        src={SVG.editGrayIcon}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  }
                  onClick={() => {
                    setPersonalNotifSnapshot({
                      fromDepts: [...notifyFromDepts],
                      quickDepts: [...notifyQuickDepts],
                      types: [...notifyTypes],
                    });
                    setShowPersonalNotif(true);
                  }}
                >
                  הגדרה
                </AppButton>
              </div>
            </div>

            <p className="my-2 font-medium">פרטי יצירת קשר</p>

            <div className="flex items-start gap-3">
              <div className="flex items-center flex-1">
                <div className="w-10">
                  <img
                    src={SVG.contactDarkIcon}
                    className="w-[24px] h-[24px] object-contain"
                  />
                </div>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <AppTextInput
                      type="number"
                      label="טלפון"
                      placeholder="טלפון"
                      className="border-0"
                      required
                      {...field}
                      error={errors.phone?.message || ''}
                    />
                  )}
                />
              </div>
              <Controller
                name="countryCode"
                control={control}
                render={({ field: { value } }) => (
                  <AppMUIAutocomplete
                    size="small"
                    options={COUNTRY_CODES}
                    getOptionLabel={(o) => o.label}
                    clearIcon={null}
                    sx={{
                      '& .MuiAutocomplete-inputRoot': {
                        padding: '8px !important',
                      },
                    }}
                    renderValue={(val) =>
                      val && (
                        <div className="flex items-center gap-1">
                          <img
                            src={val.icon}
                            alt="icon"
                            className="w-[32px] h-[32px] object-contain"
                          />
                        </div>
                      )
                    }
                    renderOption={(props, option) => {
                      const { key, ...rest } = props;
                      return (
                        <li key={key} {...rest}>
                          <img
                            src={option.icon}
                            alt="icon"
                            className="w-[32px] h-[32px] object-contain"
                          />
                        </li>
                      );
                    }}
                    value={COUNTRY_CODES.find((c) => c.value === value) || null}
                    error={!!errors.countryCode}
                    helperText={errors.countryCode?.message}
                  />
                )}
              />
            </div>

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <div className="flex items-center">
                  <div className="w-10">
                    <img
                      src={SVG.emailDarkIcon}
                      className="w-[20px] h-[16px] object-contain"
                    />
                  </div>
                  <AppTextInput
                    type="text"
                    label="אימייל"
                    placeholder="אימייל"
                    className="border-0"
                    required
                    {...field}
                    error={errors.email?.message || ''}
                  />
                </div>
              )}
            />

            <div className="flex items-start">
              <div className="w-10" />
              <div className="flex items-start gap-3">
                <Controller
                  name="sendPassword"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          color="secondary"
                          size="small"
                          sx={{ p: 1 }}
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            setValue('sendPasswordVia', '', {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }}
                        />
                        <p className="m-0">שליחת סיסמה להתחברות</p>
                      </div>
                      {errors?.sendPasswordVia?.message && (
                        <span className="text-red-400 text-[12px]">
                          {errors.sendPasswordVia.message}
                        </span>
                      )}
                    </div>
                  )}
                />
                <Controller
                  name="sendPasswordVia"
                  control={control}
                  render={({ field }) => {
                    const disabled = !watch('sendPassword');
                    return (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-2">
                          <Radio
                            color="secondary"
                            size="small"
                            sx={{ p: 1 }}
                            disabled={disabled}
                            value="email"
                            checked={field.value === 'email'}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                          <p className="m-0">לאימייל</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Radio
                            color="secondary"
                            size="small"
                            sx={{ p: 1 }}
                            disabled={disabled}
                            value="sms"
                            checked={field.value === 'sms'}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                          <p className="m-0">לטלפון</p>
                        </div>
                      </div>
                    );
                  }}
                />
              </div>
            </div>

            <Controller
              name="birthday"
              control={control}
              render={({ field }) => (
                <div className="flex items-center">
                  <div className="w-10">
                    <img
                      src={SVG.birthdateDarkIcon}
                      className="w-[24px] h-[24px] object-contain"
                    />
                  </div>
                  <AppTextInput
                    type="date"
                    label="תאריך לידה"
                    placeholder="תאריך לידה"
                    className="border-0"
                    {...field}
                  />
                </div>
              )}
            />
          </div>
        )}

        <div className="flex justify-end px-[48px]">
          <div className="flex items-center gap-2">
            {showPermissionList ? (
              <>
                <AppButton
                  onClick={() => {
                    setValue('userPermissions', permissionsSnapshot);
                    setShowPermissionList(false);
                  }}
                >
                  ביטול
                </AppButton>
                <AppButton
                  variant="primary"
                  onClick={() => setShowPermissionList(false)}
                >
                  החל
                </AppButton>
              </>
            ) : showPersonalNotif ? (
              <>
                <AppButton onClick={onCancelPersonalNotifChanges}>
                  ביטול
                </AppButton>
                <AppButton
                  variant="primary"
                  onClick={() => setShowPersonalNotif(false)}
                >
                  שמור את הגדרות ההתראות
                </AppButton>
              </>
            ) : (
              <>
                <AppButton onClick={handleModalClose}>ביטול</AppButton>
                <AppButton
                  variant="primary"
                  isLoading={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                >
                  שמירה
                </AppButton>
              </>
            )}
          </div>
        </div>
      </div>
    </AppDialog>
  );
}

export default AddEditUserDialog;
